import { Box, Divider, Grid, alpha, useTheme } from '@mui/material'
import { FC, Fragment } from 'react'
import HeaderField from './HeaderField'
import { EntityHeaderProps } from './types'

/**
 * A reusable multi-column header component for entity detail views.
 * Displays field labels and values in a responsive grid layout.
 *
 * @example
 * const columns: HeaderColumnConfig[] = [
 *   {
 *     key: 'dates',
 *     fields: [
 *       { label: 'Est Launch Date', value: deal?.estLaunchDate, format: formatDate },
 *       { label: 'Start Date', value: deal?.startDate, format: formatDate },
 *     ]
 *   },
 *   {
 *     key: 'status',
 *     fields: [
 *       { label: 'Status', value: deal?.status, getColor: renderColorByString },
 *     ]
 *   },
 * ];
 *
 * <EntityHeader columns={columns} />
 */
const EntityHeader: FC<EntityHeaderProps> = ({
  columns,
  showDividers = true,
  padding = 3,
  boxShadow: boxShadowProp,
  borderRadius = '8px',
  sx,
}) => {
  const theme = useTheme()
  const boxShadow = boxShadowProp ?? `0 2px 6px ${alpha(theme.palette.text.primary, 0.1)}`

  return (
    <Box
      sx={{
        p: padding,
        borderRadius,
        boxShadow,
        ...sx,
      }}
    >
      <Grid
        container
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          overflowY: 'hidden',
        }}
      >
        {columns.map((column, index) => {
          const { key, fields, gridSize } = column
          const isLast = index === columns.length - 1

          // Default grid sizes
          const sizes = {
            xs: gridSize?.xs ?? 12,
            sm: gridSize?.sm ?? 12,
            md: gridSize?.md ?? 12,
            lg: gridSize?.lg ?? 3,
            xl: gridSize?.xl ?? 3,
          }

          return (
            <Fragment key={key}>
              <Grid
                item
                xs={sizes.xs}
                sm={sizes.sm}
                md={sizes.md}
                lg={sizes.lg}
                xl={sizes.xl}
                sx={{ py: 1, px: 1 }}
              >
                {fields.map((field, fieldIndex) => (
                  <HeaderField
                    key={`${key}-${field.label}-${fieldIndex}`}
                    {...field}
                    theme={theme}
                  />
                ))}
              </Grid>
              {showDividers && !isLast && (
                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
              )}
            </Fragment>
          )
        })}
      </Grid>
    </Box>
  )
}

export default EntityHeader
